// view/mediaControls.js — the shared base of the own media controls (plan bugfix_2026-10-14_polishing-patch, phase
// video-skip). Video (videoControls.js) and audio (audioControls.js) build on it and add their own parts:
//
//   createMediaControlsBase({ media, prefix, size, text }) → play/pause, skip back / forward (click 1 s, held faster
//       up to 10 s per step — boss 192) and sound on/off, bound to ONE media element and following its own events
//       (other code may play, pause or mute it too). The caller lays out the row, adds its parts with the returned
//       `on` and ends everything with disposers.dispose().
//   createMediaSeekBar({ media, label, inline }) → the seek slider (video: over the bottom edge of the video; audio:
//       inline in the row).
//   iconButton / setIcon → the topo2 icon buttons of the controls.

import { createDisposers } from '../core/disposers.js';
import { bindHoldRepeat } from './holdRepeat.js';

export const ICON_BUTTON = 'noshadow topo2-btn topo2-btn--icon topo2-btn--solid topo2-btn--fullbleed';

export function bindTo(disposers) {
    return (node, ev, fn) => { node.addEventListener(ev, fn); disposers.add(() => node.removeEventListener(ev, fn)); };
}

// size: the size classes of the row (video: topo2-btn--small; audio: topo2-btn--extrasmall topo2-btn--inset).
export function iconButton(cls, title, size) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `${ICON_BUTTON} ${size} ${cls}`;
    b.title = title;
    b.setAttribute('aria-label', title);
    b.innerHTML = '<span class="topo2-ic"></span>';
    return b;
}

export function setIcon(button, name) {
    button.querySelector('.topo2-ic').className = `topo2-ic topo2-ic--${name}`;
}

// prefix: 'video' | 'audio' → the buttons' own classes (topo2-video-play, topo2-audio-sound, …).
// text: the labels, already merged with the caller's defaults (playPause, skipBack, skipForward, soundOff, soundOn).
export function createMediaControlsBase({ media, prefix, size, text }) {
    const disposers = createDisposers();
    const on = bindTo(disposers);

    const playBtn        = iconButton(`topo2-${prefix}-play`, text.playPause, size);
    const skipBackBtn    = iconButton(`topo2-${prefix}-skip-back`, text.skipBack, size);
    const skipForwardBtn = iconButton(`topo2-${prefix}-skip-forward`, text.skipForward, size);
    const soundBtn       = iconButton(`topo2-${prefix}-sound`, text.soundOff, size);
    setIcon(skipBackBtn, 'rewind');
    setIcon(skipForwardBtn, 'fast-forward');

    // A step of n seconds, clamped to the media; playing media keeps playing, paused stays paused.
    const skipBy = (seconds) => {
        const end = Number.isFinite(media.duration) ? media.duration : 0;
        media.currentTime = Math.max(0, Math.min(end, media.currentTime + seconds));
    };
    // While a skip button is held the media pauses — playing on between the steps would undo part of a skip back —
    // and resumes on release if it was playing.
    let resumeAfterHold = false;
    const holdStart = () => {
        resumeAfterHold = !media.paused;
        if (resumeAfterHold) { media.pause(); }
    };
    const holdEnd = () => {
        if (resumeAfterHold) { media.play(); }
        resumeAfterHold = false;
    };
    disposers.add(bindHoldRepeat(skipBackBtn, { onStep: (s) => skipBy(-s), onStart: holdStart, onEnd: holdEnd }).destroy);
    disposers.add(bindHoldRepeat(skipForwardBtn, { onStep: (s) => skipBy(s), onStart: holdStart, onEnd: holdEnd }).destroy);

    const showPlayState = () => setIcon(playBtn, media.paused ? 'play' : 'pause');
    const showSound = () => {
        setIcon(soundBtn, media.muted ? 'volume-off' : 'volume');
        soundBtn.title = media.muted ? text.soundOn : text.soundOff;
        soundBtn.setAttribute('aria-label', soundBtn.title);
    };

    on(playBtn, 'click', () => { if (media.paused) { media.play(); } else { media.pause(); } });
    on(soundBtn, 'click', () => { media.muted = !media.muted; });

    on(media, 'play', showPlayState);
    on(media, 'pause', showPlayState);
    on(media, 'ended', showPlayState);
    on(media, 'volumechange', showSound);

    showPlayState();
    showSound();

    return { playBtn, skipBackBtn, skipForwardBtn, soundBtn, on, disposers };
}

export function createMediaSeekBar({ media, label = 'Springen', inline = false } = {}) {
    const disposers = createDisposers();
    const on = bindTo(disposers);

    const element = document.createElement('div');
    element.className = inline ? 'topo2-video-seek topo2-video-seek--inline' : 'topo2-video-seek';
    const range = document.createElement('input');
    range.type = 'range';
    range.className = 'topo2-video-seek-range';
    range.min = '0';
    range.step = '0.1';
    range.setAttribute('aria-label', label);
    range.title = label;
    element.appendChild(range);

    // While the thumb is dragged, playback must not pull it back.
    let dragging = false;

    const showProgress = () => {
        const max = Number(range.max) || 0;
        element.style.setProperty('--topo2-seek-progress', max ? `${(Number(range.value) / max) * 100}%` : '0%');
    };
    const showDuration = () => {
        range.max = String(Number.isFinite(media.duration) ? media.duration : 0);
        showProgress();
    };
    const showTime = () => {
        if (dragging) { return; }
        range.value = String(media.currentTime || 0);
        showProgress();
    };

    on(range, 'input', () => { dragging = true; media.currentTime = Number(range.value); showProgress(); });
    on(range, 'change', () => { dragging = false; });
    // The slider lies over the tag layer: a press on it must not reach anything below (drawing, the layer click that
    // plays/pauses).
    on(element, 'mousedown', (ev) => ev.stopPropagation());
    on(element, 'click', (ev) => ev.stopPropagation());

    on(media, 'timeupdate', showTime);
    on(media, 'seeked', showTime);
    on(media, 'loadedmetadata', showDuration);
    on(media, 'durationchange', showDuration);

    showDuration();
    showTime();

    return { element, destroy() { disposers.dispose(); } };
}
